<template lang="html">
  <div v-if="loading" class="loader-container">
    <loader></loader>
  </div>
  <v-app v-else class="large-screen__body white">
    <div>
      <v-toolbar flat>
        <!-- left icons -->
        <div class="back-button-toolbar">
          <v-btn icon class="toolbar-button" @click="goBack()">
            <img src="~icons/backIcon/backIcon.png" alt="backIcon">
          </v-btn>
        </div>
        <v-spacer></v-spacer>
        <!-- center text -->
        <v-toolbar-title class="white--text" v-if="user.user_id">
          COMPLETION BY {{ user.username }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
      </v-toolbar>
    </div>
    <div flat class="sub-toolbar">
      <v-layout row wrap>
        <v-flex class="pt-3 completion-date">
          Completed on {{ dateFormat(completion_summary.completion_finished) }}
        </v-flex>
        <v-flex class="pt-2 feedback-btn" v-if="allowReview">
          <v-btn small class="fixed-primary-button white--text" @click="review=true">
            Rate & Leave Feedback
          </v-btn>
        </v-flex>
      </v-layout>
    </div>

    <div class="user-rating" v-if="reviewer_id">
      <div>
        <v-icon class="user-rating__star" v-for="i in rating" :key="i">star</v-icon>
      </div>

      <p class="user-rating__comment">
        {{ comment }}
      </p>

      <router-link class="user-rating__reviewer" :to="{ name: 'UserProfile', params: {id: reviewer_id}}">
        - @{{reviewer_name}}
      </router-link>
    </div>

    <div v-for="(completion_item, index) in tasks_completion_items" class="question">
      <div class="pb-3">
        <strong>
          {{ index + 1 }}.
          {{ completion_item.task_completion_text }}
        </strong>
      </div>

      <div v-if="getTaskCompletionType(completion_item.task_completion_type_id) === 'location'">
        <google-map :latitude="latitude(completion_item.task_completion_item_id)" :longitude="longitude(completion_item.task_completion_item_id)">
        </google-map>
        {{ getAddressFromLatLng(completion_item.task_completion_item_id) }}
        {{ getTaskCompletionAnswer(completion_item.task_completion_item_id, 'location') }}
      </div>
      <div  v-else-if="getTaskCompletionType(completion_item.task_completion_type_id) === 'upload'">
        <img :src="uploadImage(completion_item.task_completion_item_id)" alt="Upload Image" style="max-height: 300px">
      </div>
      <div v-else>
        {{ getTaskCompletionAnswer(completion_item.task_completion_item_id, getTaskCompletionType(completion_item.task_completion_type_id)) }}
      </div>
    </div>

    <v-dialog hide-overlay v-model="review" max-width="500px">
      <task-completion-review @submit="submitReview"/>
    </v-dialog>
  </v-app>
</template>

<script>
import moment from 'moment';
import get from 'lodash/get';
import { getTaskCompletionDetails, submitCompletionReview } from '@/api';

import Loader from '@/components/Loader';
import GoogleMap from '@/components/googleMap';
import TaskCompletionReview from '@/components/TaskCompletionReview';

export default {
  components: {
    TaskCompletionReview,
    Loader,
    GoogleMap,
  },
  data() {
    return {
      completion_summary: {},
      completions: [],
      tasks_completion_items: [],
      task_completion_types: [],
      reviewer_data: {},
      tasks_organizers: [],
      user: {},
      loading: false,
      review: false,
    };
  },
  computed: {
    rating() {
      return parseInt(this.completion_summary.completion_rating, 10);
    },
    comment() {
      return this.completion_summary.completion_rating_comment;
    },
    reviewer_id() {
      return this.reviewer_data.user_id;
    },
    reviewer_name() {
      return this.reviewer_data.username;
    },
    token() {
      return this.$store.getters['auth/userToken'];
    },
    allowReview() {
      // if not reviewed and current user is in organizer
      if (this.reviewer_id) {
        return false;
      }

      const selfInReviewer = this.tasks_organizers.find(o => o.user_id === this.$store.getters['auth/userId']);

      if (selfInReviewer) {
        return true;
      }

      return false;
    },
  },
  methods: {
    submitReview: async function submitReview({ rating, comment }) {
      this.submittingReview = true;
      const formData = {
        completion_id: this.completion_summary.completion_id,
        completion_rating: rating,
        completion_rating_comment: comment,
      };

      const { data } = await submitCompletionReview(this.token, formData);
      const status = get(data, '[1].status');
      if (status === 'SUCCESS') {
        this.review = false;
        const message = 'Review submitted succesfully';
        this.$store.dispatch('auth/setFlashMessage', {
          message,
          type: 'success',
        });
        this.fetchTaskCompletionDetails();
      } else {
        const message = get(data, '[1].error.error_usr_msg', 'Failed to submit your review');
        this.$store.dispatch('auth/setFlashMessage', {
          message,
          type: 'error',
        });
      }
    },
    fetchTaskCompletionDetails: async function fetchTaskCompletionDetails() {
      const token = this.token;
      this.loading = true;
      try {
        const response = await getTaskCompletionDetails(token, this.$route.params.id);
        this.loading = false;
        const details = get(response, 'data[1].data.details', {});

        this.completions = get(details, 'completion_bits_data', []);
        this.tasks_completion_items = get(details, 'task_data[0].tasks_completion_items', []);
        this.task_completion_types = get(details, 'tasks_completion_types', []);
        this.completion_summary = get(details, 'completion_data', {});
        this.reviewer_data = get(details, 'reviewer_user_data', {});
        this.user = get(details, 'user_data', {});
        this.tasks_organizers = get(details, 'task_data[0].tasks_organizers', []);
      } catch (e) {
        this.loading = false;
      }
    },
    completion(id) {
      return this.completions.find(c => c.task_completion_item_id === id) || {};
    },
    latitude(id) {
      return this.completion(id).location_lat;
    },
    longitude(id) {
      return this.completion(id).location_lng;
    },
    getAddressFromLatLng(id) {
      if (this.completion(id).address) return;

      const geocoder = new window.google.maps.Geocoder();
      const location = {
        lat: parseFloat(this.latitude(id)),
        lng: parseFloat(this.longitude(id)),
      };

      geocoder.geocode({ location }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const completion = this.completion(id);
          completion.address = results[0].formatted_address;
          this.completions = [...this.completions];
        }
      });
    },
    uploadImage(id) {
      const completion = this.completion(id);
      return get(completion, 'completions_data[0].data_url.url');
    },
    getTaskCompletionAnswer(id, answerType) {
      const completion = this.completion(id);

      switch (answerType) {
        case 'none':
        case 'text_short':
        case 'vote':
        case 'hyperlink':
        case 'multiselect':
          return completion.completion_value;
        case 'address':
          return completion.location_address;
        case 'location':
          return completion.address;
        case 'number_range':
          return parseFloat(get(completion, 'completion_value_float', 0));
        case 'date':
          return moment(completion.completion_date).format('DD MMMM YYYY');
        case 'skills_single':
          return get(completion, 'completions_skills[0].skill_name');
        case 'text_long':
          return completion.completion_value_long;
        default:
          return '';
      }
    },
    getTaskCompletionType(typeId) {
      const type = this.task_completion_types.find(t => t.task_completion_type_id === typeId) || {};
      return type.completion_type_short;
    },
    goBack() {
      this.$router.go(-1);
    },
    dateFormat(time) {
      if (!time) return '-';
      return moment(time).format('DD MMMM YYYY, hh:mm a');
    },
  },
  mounted() {
    this.fetchTaskCompletionDetails();
  },
};
</script>

<style lang="scss" scoped>
@import "~styles/shared.scss";

.sub-toolbar {
  background-color: $gray;
  min-height: 48px;
}

.loader-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-top: 50px;
}

.fixed-primary-button {
  background: $primary;
  font-family: MuseoSans-900;
  text-align: center;
  border-radius: 24px;
  padding: 1px;
}

.feedback-btn {
  text-align: center;
  padding-right: 10px;
  padding-bottom: 10px;
}

.completion-date {
  text-align: center;
  padding-left: 30px;
  font-size: 15px;
}

.question {
  border-bottom: 1px solid $gray;
  padding: 20px 30px 20px 30px;
}

.question:last-child {
  border-bottom: 0px;
}

.user-rating {
  padding: 10px;
  background: $lighter;
  text-align: center;
}

.user-rating__star {
  color: $primary-yello;
}

.user-rating__comment {
  font-style: italic;
  font-size: 14px;
}

.user-rating__reviewer {
  font-weight: bold;
  color: $blue;
  text-decoration: none;
}

</style>



// WEBPACK FOOTER //
// src/components/TaskCompletionDetails.vue